/**
 * Card Payments API Service
 *
 * Handles Paystack card payment integration including:
 * - Initialize subscription payment
 * - List saved cards
 * - Charge saved card (recurring)
 * - Delete saved card
 */

import { post } from './client';
import { getLandlordId, getUserEmail } from './client';

/**
 * Initialize a new card payment for subscription
 * @param {Object} params - Payment parameters
 * @param {number} params.amount - Amount in KES
 * @param {boolean} params.saveCard - Whether to save card for future payments
 * @param {string} params.subscriptionPlan - Plan name: STANDARD, PREMIUM, ANNUAL
 * @returns {Promise<Object>} Response with authorization_url, reference, access_code, public_key
 */
export const initializeSubscription = async ({
    amount,
    saveCard = true,
    subscriptionPlan = 'STANDARD',
}) => {
    const landlordId = getLandlordId();
    const email = getUserEmail();

    if (!landlordId) {
        throw new Error('Landlord ID not found');
    }

    if (!email) {
        throw new Error('Email not found');
    }

    const response = await post('pegasus/visionary/payments/card/subscription/initialize', {
        landlord_id: parseInt(landlordId),
        amount: amount,
        email: email,
        save_card: saveCard,
        subscription_plan: subscriptionPlan,
    }, { requiresAuth: false });

    return response;
};

/**
 * Get list of saved cards for the current landlord
 * @returns {Promise<Object>} Response with cards array and count
 */
export const getSavedCards = async () => {
    const landlordId = getLandlordId();

    if (!landlordId) {
        throw new Error('Landlord ID not found');
    }

    const response = await post('pegasus/visionary/payments/card/cards/list', {
        landlord_id: parseInt(landlordId),
    });

    return response;
};

/**
 * Charge a saved card for recurring payment
 * @param {Object} params - Charge parameters
 * @param {number} params.cardTokenId - The saved card ID
 * @param {number} params.amount - Amount in KES
 * @param {string} params.description - Payment description
 * @returns {Promise<Object>} Response with reference, message, amount, card
 */
export const chargeSavedCard = async ({
    cardTokenId,
    amount,
    description = 'Subscription payment',
}) => {
    const landlordId = getLandlordId();

    if (!landlordId) {
        throw new Error('Landlord ID not found');
    }

    const response = await post('pegasus/visionary/payments/card/charge/recurring', {
        card_token_id: cardTokenId,
        landlord_id: parseInt(landlordId),
        amount: amount,
        description: description,
    });

    return response;
};

/**
 * Delete a saved card
 * @param {number} cardTokenId - The saved card ID to delete
 * @returns {Promise<Object>} Response with success status and message
 */
export const deleteSavedCard = async (cardTokenId) => {
    const landlordId = getLandlordId();

    if (!landlordId) {
        throw new Error('Landlord ID not found');
    }

    const response = await post('pegasus/visionary/payments/card/cards/delete', {
        card_token_id: cardTokenId,
        landlord_id: parseInt(landlordId),
    });

    return response;
};

/**
 * Get card type icon name based on card type
 * @param {string} cardType - Card type from API (visa, mastercard, verve, etc.)
 * @returns {string} Icon identifier
 */
export const getCardTypeIcon = (cardType) => {
    const type = cardType?.toLowerCase();
    switch (type) {
        case 'visa':
            return 'visa';
        case 'mastercard':
            return 'mastercard';
        case 'verve':
            return 'verve';
        default:
            return 'generic';
    }
};

/**
 * Format card number for display
 * @param {string} last4 - Last 4 digits of card
 * @returns {string} Formatted card number
 */
export const formatCardNumber = (last4) => {
    return `•••• •••• •••• ${last4}`;
};

export default {
    initializeSubscription,
    getSavedCards,
    chargeSavedCard,
    deleteSavedCard,
    getCardTypeIcon,
    formatCardNumber,
};
